import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import Card from '../../components/Card';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
`;

const StatsCard = styled(Card)`
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const RecentActivity = styled.div`
  margin-top: 2rem;
`;

const ActivityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ActivityItem = styled.li`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityTime = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-left: 0.5rem;
`;

const AdminDashboard = () => {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalZones: 0,
    pendingTasks: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch dashboard data
    const fetchDashboardData = async () => {
      try {
        // In a real app, these would be API calls
        // For now, we'll use mock data
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setStats({
          totalUsers: 5,
          activeUsers: 3,
          totalZones: 8,
          pendingTasks: 12
        });
        
        setRecentActivity([
          { id: 1, action: 'User login', user: 'admin', timestamp: new Date().toISOString() },
          { id: 2, action: 'Zone updated', user: 'john', timestamp: new Date(Date.now() - 3600000).toISOString() },
          { id: 3, action: 'Task completed', user: 'sarah', timestamp: new Date(Date.now() - 7200000).toISOString() },
          { id: 4, action: 'New user created', user: 'admin', timestamp: new Date(Date.now() - 86400000).toISOString() }
        ]);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.round((date - new Date()) / (1000 * 60 * 60)),
      'hour'
    );
  };

  if (loading) {
    return <div>Loading dashboard data...</div>;
  }

  return (
    <div>
      <PageHeader>
        <Title>Admin Dashboard</Title>
        <Subtitle>Welcome back, {user?.username || 'Admin'}</Subtitle>
      </PageHeader>

      <DashboardContainer>
        <StatsCard>
          <StatValue>{stats.totalUsers}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatsCard>
        
        <StatsCard>
          <StatValue>{stats.activeUsers}</StatValue>
          <StatLabel>Active Users</StatLabel>
        </StatsCard>
        
        <StatsCard>
          <StatValue>{stats.totalZones}</StatValue>
          <StatLabel>Total Zones</StatLabel>
        </StatsCard>
        
        <StatsCard>
          <StatValue>{stats.pendingTasks}</StatValue>
          <StatLabel>Pending Tasks</StatLabel>
        </StatsCard>
      </DashboardContainer>

      <RecentActivity>
        <h2>Recent Activity</h2>
        <Card>
          <ActivityList>
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <ActivityItem key={activity.id}>
                  <strong>{activity.action}</strong> by {activity.user}
                  <ActivityTime>{formatTime(activity.timestamp)}</ActivityTime>
                </ActivityItem>
              ))
            ) : (
              <ActivityItem>No recent activity</ActivityItem>
            )}
          </ActivityList>
        </Card>
      </RecentActivity>
    </div>
  );
};

export default AdminDashboard;
